const getSessionStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    return window.sessionStorage;
  } catch (err) {
    console.warn('[ttlCache] sessionStorage unavailable:', err);
    return null;
  }
};

const serializeMap = (map) => {
  const entries = [];
  map.forEach((value, key) => {
    entries.push([key, value]);
  });
  return JSON.stringify(entries);
};

const deserializeMap = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((entry) => Array.isArray(entry) && entry.length === 2);
  } catch (err) {
    console.warn('[ttlCache] Failed to parse cache payload:', err);
    return [];
  }
};

const pruneExpiredEntries = (map, now) => {
  const removedKeys = [];
  map.forEach((entry, key) => {
    const entryTtl = typeof entry.ttl === 'number' ? entry.ttl : 0;
    if (!entry || !entry.timestamp || (entryTtl > 0 && now - entry.timestamp > entryTtl)) {
      removedKeys.push(key);
      map.delete(key);
    }
  });
  return removedKeys;
};

const ensureMaxEntries = (map, maxEntries) => {
  if (!maxEntries || map.size <= maxEntries) {
    return [];
  }
  const removed = [];
  const iterator = map.keys();
  while (map.size > maxEntries) {
    const { value, done } = iterator.next();
    if (done) {
      break;
    }
    removed.push(value);
    map.delete(value);
  }
  return removed;
};

const persistMap = (map, storageKey) => {
  const storage = getSessionStorage();
  if (!storage || !storageKey) {
    return;
  }
  try {
    storage.setItem(storageKey, serializeMap(map));
  } catch (err) {
    console.warn('[ttlCache] Failed to persist cache:', err);
  }
};

const loadMap = (map, storageKey) => {
  const storage = getSessionStorage();
  if (!storage || !storageKey) {
    return;
  }
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) {
      return;
    }
    const entries = deserializeMap(raw);
    entries.forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        map.set(key, value);
      }
    });
  } catch (err) {
    console.warn('[ttlCache] Failed to load cache:', err);
  }
};

const createTtlCache = ({ storageKey, ttl = 5 * 60 * 1000, maxEntries = 500 } = {}) => {
  const memory = new Map();
  let initialized = false;

  const ensureInitialized = () => {
    if (initialized) {
      return;
    }
    loadMap(memory, storageKey);
    initialized = true;
  };

  const removeExpired = () => {
    const now = Date.now();
    const removed = pruneExpiredEntries(memory, now);
    if (removed.length && storageKey) {
      persistMap(memory, storageKey);
    }
  };

  const get = (key) => {
    ensureInitialized();
    const entry = memory.get(key);
    if (!entry) {
      return null;
    }
    const entryTtl = typeof entry.ttl === 'number' ? entry.ttl : ttl;
    const isExpired = entryTtl > 0 && Date.now() - entry.timestamp > entryTtl;
    if (isExpired) {
      memory.delete(key);
      if (storageKey) {
        persistMap(memory, storageKey);
      }
      return null;
    }
    return entry.value;
  };

  const set = (key, value, customTtl) => {
    ensureInitialized();
    const entry = {
      value,
      timestamp: Date.now(),
      ttl: typeof customTtl === 'number' ? customTtl : ttl
    };
    memory.set(key, entry);
    removeExpired();
    ensureMaxEntries(memory, maxEntries);
    if (storageKey) {
      persistMap(memory, storageKey);
    }
  };

  const deleteKey = (key) => {
    ensureInitialized();
    const deleted = memory.delete(key);
    if (deleted && storageKey) {
      persistMap(memory, storageKey);
    }
    return deleted;
  };

  const clear = () => {
    ensureInitialized();
    memory.clear();
    const storage = getSessionStorage();
    if (storage && storageKey) {
      storage.removeItem(storageKey);
    }
  };

  return {
    get,
    set,
    delete: deleteKey,
    clear,
    ttl,
  };
};

export default createTtlCache;
